import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Message } from "primereact/message";

import { ReminderActionResult } from "../../../../hooks/useProspectReminders";
import { ReminderOwner, ReminderRung } from "../../../../types/onboarding";
import { previewTypeFor } from "../../../../utils/reminders";

interface SendNextReminderDialogProps {
  visible: boolean;
  rung: ReminderRung;
  owner: ReminderOwner;
  onHide: () => void;
  onSend: () => Promise<ReminderActionResult>;
  // Reminder emails only: hands over to the email preview, whose Send sends the same rung.
  onPreview: () => void;
}

/**
 * CWA-189: confirm sending the next reminder rung now, ahead of its day. The
 * rung is recorded as sent manually and never goes out a second time.
 */
const SendNextReminderDialog: React.FC<SendNextReminderDialogProps> = ({
  visible,
  rung,
  owner,
  onHide,
  onSend,
  onPreview,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const email = previewTypeFor(rung) !== null;

  useEffect(() => {
    if (!visible) {
      setError(null);
      setSubmitting(false);
    }
  }, [visible]);

  const send = async () => {
    setSubmitting(true);
    setError(null);
    const result = await onSend();
    setSubmitting(false);
    if (result.ok) onHide();
    else setError(result.message ?? null);
  };

  const footer = (
    <div>
      <Button label="Cancel" text onClick={onHide} disabled={submitting} />
      {email && <Button label="Preview" icon="pi pi-eye" outlined onClick={onPreview} disabled={submitting} />}
      <Button label="Send now" icon="pi pi-send" onClick={send} loading={submitting} disabled={submitting} />
    </div>
  );

  return (
    <Dialog header="Send next reminder" visible={visible} onHide={onHide} footer={footer} style={{ width: "30rem" }}>
      {error && <Message severity="error" className="w-full mb-3" text={error} />}
      <p className="mt-0">
        Send <strong>{rung.label}</strong> now?{" "}
        {email
          ? "The customer gets the reminder email straight away."
          : owner === "CUSTOMER"
          ? "It is posted to the onboarding Slack channel; the customer gets nothing."
          : "It is posted to the onboarding Slack channel."}
      </p>
      <p className="mb-0 text-500">It is recorded as sent manually and is not sent again on its due day.</p>
    </Dialog>
  );
};

export default SendNextReminderDialog;
